import React, { useEffect, useState } from 'react';
import {
  Box,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  VStack,
  Text,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { addCardLabel, removeCardLabel } from '../../../../ProjectBoardServices/BoardModalAPI/LeftModalAPI/LabelsAPI';

const Labels = ({ task, onUpdate }) => {
  const toast = useToast();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  const [labels, setLabels] = useState(Array.isArray(task?.labels) ? task.labels : []);
  useEffect(() => {
    setLabels(Array.isArray(task?.labels) ? task.labels : []);
  }, [task]);

  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [removingTitle, setRemovingTitle] = useState(null);
  const [labelToDelete, setLabelToDelete] = useState(null);

  const [newLabel, setNewLabel] = useState({
    title: '',
    color: '#FFFFFF',
    background_color: '#3182CE',
  });

  const cardId = task?.id;

  const getContrastColor = (hexColor) => {
    if (!hexColor || hexColor.length < 7) return '#000000';
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  const notifyParent = (updated) => {
    if (typeof onUpdate === 'function') {
      onUpdate(updated);
    }
  };

  const handleRemove = async (labelTitle) => {
    if (!cardId) return;
    try {
      setRemovingTitle(labelTitle);
      const res = await removeCardLabel(cardId, labelTitle);
      if (res.status === 200) {
        const updated = labels.filter((l) => l.title !== labelTitle);
        setLabels(updated);
        notifyParent(updated);
        toast({ title: 'Label removed', status: 'success', duration: 2000 });
      } else {
        throw new Error('Remove label failed');
      }
    } catch (err) {
      toast({ title: 'Error removing label', status: 'error', duration: 2500 });
    } finally {
      setRemovingTitle(null);
      onDeleteClose();
    }
  };

  const openDeleteConfirmation = (labelTitle) => {
    setLabelToDelete(labelTitle);
    onDeleteOpen();
  };
  const confirmDelete = () => {
    if (labelToDelete) {
      handleRemove(labelToDelete);
      setLabelToDelete(null);
    }
  };

  const handleAdd = async () => {
    if (!cardId) return;
    if (!newLabel.title.trim()) {
      toast({ title: 'Label title is required', status: 'warning', duration: 2000 });
      return;
    }
    const exists = labels.some((l) => l.title.toLowerCase() === newLabel.title.trim().toLowerCase());
    if (exists) {
      toast({ title: 'Label already exists', status: 'info', duration: 2000 });
      return;
    }
    try {
      setIsAdding(true);
      const res = await addCardLabel(cardId, {
        title: newLabel.title.trim(),
        color: newLabel.color,
        background_color: newLabel.background_color,
      });
      if (res.status === 200) {
        const updated = [...labels, {
          title: newLabel.title.trim(),
          color: newLabel.color,
          background_color: newLabel.background_color,
        }];
        setLabels(updated);
        notifyParent(updated);
        toast({ title: 'Label added', status: 'success', duration: 2000 });
        setNewLabel({ title: '', color: '#FFFFFF', background_color: '#3182CE' });
        setIsOpen(false);
      } else {
        throw new Error('Add label failed');
      }
    } catch (err) {
      toast({ title: 'Error adding label', status: 'error', duration: 2500 });
    } finally {
      setIsAdding(false);
    }
  };

  const resetModal = () => {
    setNewLabel({ title: '', color: '#FFFFFF', background_color: '#3182CE' });
    setIsOpen(false);
  };

  return (
    <Box>
      <HStack spacing={2} flexWrap="wrap">
        {Array.isArray(labels) && labels.map((label, index) => (
          <Tag
            key={`${label.title}-${index}`}
            px={3}
            py={1}
            borderRadius="12px"
            bg={label.background_color || '#E3F2FD'}
            color={label.color || getContrastColor(label.background_color || '#E3F2FD')}
            fontSize="12px"
            fontWeight="500"
            textTransform="capitalize"
            cursor="default"
            opacity={removingTitle === label.title ? 0.6 : 1}
          >
            <TagLabel>{label.title}</TagLabel>
            <TagCloseButton
              aria-label={`Remove ${label.title}`}
              isDisabled={removingTitle === label.title}
              onClick={() => openDeleteConfirmation(label.title)}
            />
          </Tag>
        ))}
        <Button
          size="sm"
          variant="outline"
          borderColor="#DFE1E6"
          color="#6B778C"
          fontSize="12px"
          h="28px"
          px={3}
          _hover={{ bg: '#F4F5F7' }}
          onClick={() => setIsOpen(true)}
        >
          + Add Label
        </Button>
      </HStack>

      <Modal isOpen={isOpen} onClose={resetModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Label</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Input
                placeholder="Label title..."
                value={newLabel.title}
                onChange={(e) => setNewLabel({ ...newLabel, title: e.target.value })}
              />

              <Box>
                <Text fontSize="sm" fontWeight="600" mb={2} color="gray.600">
                  Preview:
                </Text>
                <Tag
                  px={3}
                  py={1}
                  borderRadius="12px"
                  bg={newLabel.background_color}
                  color={newLabel.color}
                  fontSize="12px"
                  fontWeight="500"
                >
                  <TagLabel>{newLabel.title || 'Label Preview'}</TagLabel>
                </Tag>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="600" mb={2} color="gray.600">
                  Colors:
                </Text>
                <VStack spacing={3}>
                  <HStack spacing={3} w="100%">
                    <Box flex={1}>
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        🎨 Background Color
                      </Text>
                      <HStack>
                        <Box
                          w="35px"
                          h="35px"
                          bg={newLabel.background_color}
                          borderRadius="full"
                          border="3px solid"
                          borderColor="gray.200"
                          cursor="pointer"
                          position="relative"
                          overflow="hidden"
                        >
                          <Input
                            type="color"
                            value={newLabel.background_color}
                            onChange={(e) =>
                              setNewLabel({ ...newLabel, background_color: e.target.value })
                            }
                            w="100%"
                            h="100%"
                            border="none"
                            cursor="pointer"
                            opacity={0}
                            position="absolute"
                            top={0}
                            left={0}
                          />
                        </Box>
                        <Text fontSize="xs" color="gray.400">
                          {newLabel.background_color.toUpperCase()}
                        </Text>
                      </HStack>
                    </Box>

                    <Box flex={1}>
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        ✏️ Text Color
                      </Text>
                      <HStack>
                        <Box
                          w="35px"
                          h="35px"
                          bg={newLabel.color}
                          borderRadius="full"
                          border="3px solid"
                          borderColor="gray.200"
                          cursor="pointer"
                          position="relative"
                          overflow="hidden"
                        >
                          <Input
                            type="color"
                            value={newLabel.color}
                            onChange={(e) => setNewLabel({ ...newLabel, color: e.target.value })}
                            w="100%"
                            h="100%"
                            border="none"
                            cursor="pointer"
                            opacity={0}
                            position="absolute"
                            top={0}
                            left={0}
                          />
                        </Box>
                        <Text fontSize="xs" color="gray.400">
                          {newLabel.color.toUpperCase()}
                        </Text>
                      </HStack>
                    </Box>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleAdd}
              isLoading={isAdding}
              isDisabled={!newLabel.title.trim()}
              loadingText="Adding..."
            >
              Add Label
            </Button>
            <Button variant="ghost" onClick={resetModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove Label
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to remove the label ?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={removingTitle === labelToDelete}
                loadingText="Removing..."
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Labels;
